import { LinearProgress, Stack, Toolbar, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ResultAlert } from './Alert/ResultAlert';
import { Header } from './Header';
import { Navbar } from './Navbar/Navbar';
import { findNavEntry } from '../utils/page';
import { recordRecentPage } from '../utils/recentPages';

const NAV_OPEN_KEY = 'dorami.navOpen';

const readNavOpen = (fallback) => {
	try {
		const stored = window.localStorage.getItem(NAV_OPEN_KEY);
		return stored === null ? fallback : stored === 'true';
	} catch {
		return fallback;
	}
};

const writeNavOpen = (value) => {
	try {
		window.localStorage.setItem(NAV_OPEN_KEY, String(value));
	} catch {
		// Storage can be unavailable (private mode); the menu still works, it just won't be remembered.
	}
};

export function Layout() {
	const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'), { noSsr: true });
	// Desktop remembers the last choice; the mobile drawer is an overlay, so it always starts closed.
	const [open, setOpen] = useState(() => (isDesktop ? readNavOpen(true) : false));

	useEffect(() => {
		if (isDesktop) writeNavOpen(open);
	}, [isDesktop, open]);

	const { pathname } = useLocation();

	useEffect(() => {
		const entry = findNavEntry(pathname);
		if (entry) recordRecentPage(entry.path);
	}, [pathname]);

	const handleToggle = useCallback(() => setOpen((value) => !value), []);

	const handleOpen = useCallback(() => setOpen(true), []);

	const handleNavigate = useCallback(() => {
		if (!isDesktop) setOpen(false);
	}, [isDesktop]);

	return (
        <Box
            sx={{
                height: "100vh",
                display: 'flex'
            }}>
            <Header isOpen={open} handleToggle={handleToggle} brand="Doraemon" />
            <Navbar isOpen={open} handleToggle={handleToggle} handleOpen={handleOpen} onNavigate={handleNavigate} />
            <Stack
				component="main"
				sx={{
					width: '100%',
					minWidth: 0,
					height: '100vh',
					bgcolor: 'background.default',
				}}
			>
				<Toolbar />
				<Box
                    sx={{
                        padding: 3,
                        height: "calc(100% - 64px)",
                        overflow: 'auto'
                    }}>
					<ResultAlert />
					<Suspense fallback={<LinearProgress />}>
						<Outlet />
					</Suspense>
				</Box>
			</Stack>
        </Box>
    );
}
